import { useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api } from '../api'
import { useToast } from '../components/Toast'
import { useAsyncData } from '../hooks/useAsyncData'
import Spinner, { PageSpinner } from '../components/Spinner'
import ConfirmDialog from '../components/ConfirmDialog'
import ConnectionBadge, { connectionTitle } from '../components/ConnectionBadge'
import EditDeviceDialog from '../components/EditDeviceDialog'
import CapabilityCombobox from '../components/CapabilityCombobox'
import { capabilityMatches } from '../capabilitySearch'
import { CATEGORY_COLORS, ENV_COLORS, locationLabel } from '../constants'
import Instant from '../components/Instant'
import { formatInstant } from '../lib/time'

function Field({ label, children }) {
  return (
    <div className="min-w-0 break-words">
      <dt className="text-xs uppercase tracking-wide opacity-40">{label}</dt>
      <dd className="mt-0.5">{children ?? '—'}</dd>
    </div>
  )
}

// The device's Emporia Cloud zone, which its usage days and schedules follow. The
// cloud reads null, "UTC" and unloadable values alike as "no zone" (UTC).
function TimeZone({ device }) {
  if (device.time_zone_state === 'set') return <span className="font-mono text-xs">{device.time_zone}</span>
  return (
    <span title="Emporia treats this device as UTC">
      Not set{device.time_zone ? <span className="font-mono text-xs opacity-60"> ({device.time_zone})</span> : null}
    </span>
  )
}

// Zone changes the sync observed, newest first: drift nobody in QA made shows up here.
function ZoneChanges({ changes }) {
  if (!changes.length) return <span className="opacity-60">None seen</span>
  return (
    <ul className="space-y-0.5">
      {changes.slice(0, 3).map(c => (
        <li key={c.observed_at}>
          <span className="font-mono text-xs">{c.old_time_zone ?? 'none'} → {c.new_time_zone ?? 'none'}</span>{' '}
          <span className="opacity-60"><Instant iso={c.observed_at} style="compact" /></span>
        </li>
      ))}
    </ul>
  )
}

export default function DeviceDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [showAddForm, setShowAddForm] = useState(false)
  const [newCapId, setNewCapId] = useState('')
  const [newReason, setNewReason] = useState('')
  const [capQuery, setCapQuery] = useState('')
  const reasonRef = useRef(null)
  const [saving, setSaving] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [confirmRemove, setConfirmRemove] = useState(false)
  const [removingConstraint, setRemovingConstraint] = useState(null)
  const { show } = useToast()

  const { data, setData, loading, error, reload } = useAsyncData(async () => {
    const [device, capabilities, constraints, capCatalog, zoneChanges] = await Promise.all([
      api.getDevice(id),
      api.getDeviceCapabilities(id),
      api.getConstraints(id),
      api.getCapabilities(),
      api.getTimeZoneChanges(id),
    ])
    return { device, capabilities, constraints, capCatalog, zoneChanges }
  }, [id], { onError: err => show(err.message, 'error') })

  const device = data?.device
  const capabilities = data?.capabilities ?? []
  const constraints = data?.constraints ?? []
  const capCatalog = data?.capCatalog ?? []
  const zoneChanges = data?.zoneChanges ?? []

  async function refreshCapsAndConstraints() {
    const [caps, cons] = await Promise.all([
      api.getDeviceCapabilities(id),
      api.getConstraints(id),
    ])
    setData(d => ({ ...d, capabilities: caps, constraints: cons }))
  }

  async function addConstraint() {
    if (!newCapId) return
    setSaving(true)
    try {
      await api.createConstraint({ device_id: id, capability_id: Number(newCapId), reason: newReason || undefined })
      await refreshCapsAndConstraints()
      setShowAddForm(false)
      setNewCapId('')
      setNewReason('')
      show('Constraint added', 'success')
    } catch (err) {
      show(err.message, 'error')
    } finally {
      setSaving(false)
    }
  }

  async function removeConstraint(capabilityId) {
    if (removingConstraint != null) return
    setRemovingConstraint(capabilityId)
    try {
      await api.deleteConstraint(id, capabilityId)
      await refreshCapsAndConstraints()
    } catch (err) {
      show(err.message, 'error')
    } finally {
      setRemovingConstraint(null)
    }
  }

  async function syncDevice() {
    setSyncing(true)
    try {
      const fresh = await api.syncDevice(id)
      setData(d => ({ ...d, device: fresh }))
      show(`Synced from Emporia Cloud — ${fresh.connection}`, 'success')
    } catch (err) {
      show(err.message, 'error')
    } finally {
      setSyncing(false)
    }
  }

  async function saveDevice(changes) {
    let updated
    try {
      updated = await api.updateDevice(id, changes)
    } catch (err) {
      show(err.message, 'error')
      throw err
    }
    setData(d => ({ ...d, device: updated }))
    setEditOpen(false)
    // Effective capabilities follow category/model.
    if ('category' in changes || 'model' in changes) await refreshCapsAndConstraints()
    if ('environment' in changes) {
      // The API dropped the other environment's blob; fetch this one's now.
      try {
        const fresh = await api.syncDevice(id)
        setData(d => ({ ...d, device: fresh }))
        show(`Saved — now in ${fresh.environment}, ${fresh.connection}`, 'success')
      } catch (err) {
        show(`Saved, but couldn't fetch from ${updated.environment} cloud: ${err.message}`, 'error')
      }
    } else {
      show('Device saved', 'success')
    }
  }

  async function removeDevice() {
    setRemoving(true)
    try {
      await api.deleteDevice(id)
      navigate('/devices')
    } catch (err) {
      show(err.message, 'error')
      setRemoving(false)
      setConfirmRemove(false)
    }
  }

  const capById = new Map(capCatalog.map(c => [c.id, c]))
  const shownCapabilities = capabilities.filter(c => capabilityMatches(c, capQuery))

  if (loading) {
    return <PageSpinner />
  }

  if (error) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <Link to="/devices" className="link link-hover text-sm">← Devices</Link>
        <div className="text-center py-20">
          <p className="opacity-60 mb-4">Failed to load device: {error.message}</p>
          <button className="btn btn-sm btn-primary" onClick={reload}>Retry</button>
        </div>
      </div>
    )
  }

  if (!device) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <Link to="/devices" className="link link-hover text-sm">← Devices</Link>
        <div className="text-center py-20 opacity-40">Device not found</div>
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto">
      <Link to="/devices" className="link link-hover text-sm opacity-60">← Devices</Link>

      <div className="flex flex-wrap items-center gap-3 mt-2 mb-6">
        <h1 className="text-2xl font-bold font-mono break-all">{device.mdi}</h1>
        <span className={`badge ${CATEGORY_COLORS[device.category] ?? 'badge-neutral'}`}>{device.category}</span>
        <span className={`badge ${ENV_COLORS[device.environment] ?? 'badge-neutral'}`}>{device.environment}</span>
        <ConnectionBadge device={device} size="md" />
        {/* Actions wrap as one group, right-aligned, when the header runs out of room. */}
        <div className="flex items-center gap-2 ml-auto">
          <button className="btn btn-sm btn-outline" onClick={() => setEditOpen(true)}>
            Edit
          </button>
          <button
            className="btn btn-sm btn-outline"
            disabled={syncing}
            aria-busy={syncing}
            title={device.cloud_synced_at
              ? `Last synced ${formatInstant(device.cloud_synced_at)} — fetch from Emporia Cloud now`
              : 'Never synced — fetch from Emporia Cloud now'}
            onClick={syncDevice}
          >
            {syncing ? <Spinner size="xs" /> : 'Sync now'}
          </button>
          <button
            className="btn btn-sm btn-ghost text-error"
            disabled={removing}
            aria-busy={removing}
            onClick={() => setConfirmRemove(true)}
          >
            {removing ? <Spinner size="xs" /> : 'Remove device'}
          </button>
        </div>
      </div>

      <EditDeviceDialog
        device={device}
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSave={saveDevice}
      />

      <ConfirmDialog
        open={confirmRemove}
        title="Remove device"
        message={`Remove device "${device.mdi}" from inventory? This cannot be undone.`}
        confirmLabel="Remove"
        busy={removing}
        onConfirm={removeDevice}
        onClose={() => setConfirmRemove(false)}
      />

      {/* Device info */}
      <div className="rounded-box border border-base-300 p-5 mb-6">
        <h2 className="font-semibold mb-3">Device info</h2>
        <dl className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
          <Field label="Name">{device.name}</Field>
          <Field label="Model">{device.model}</Field>
          <Field label="Cloud model">{device.cloud_model ? <span className="font-mono text-xs">{device.cloud_model}</span> : null}</Field>
          <Field label="Location">{device.location ? locationLabel(device.location) : null}</Field>
          <Field label="Connection">{connectionTitle(device)}</Field>
          <Field label="Time zone"><TimeZone device={device} /></Field>
          <Field label="Billing day">{device.billing_cycle_start_day}</Field>
          <Field label="Zone changes"><ZoneChanges changes={zoneChanges} /></Field>
          <Field label="Created"><Instant iso={device.created_at} fallback="—" /></Field>
          <Field label="Updated"><Instant iso={device.updated_at} fallback="—" /></Field>
          <Field label="Notes">{device.notes}</Field>
        </dl>
      </div>

      {/* Effective capabilities */}
      <div className="rounded-box border border-base-300 p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold">Capabilities</h2>
          <span className="text-xs opacity-50">
            {capQuery.trim() ? `${shownCapabilities.length} of ${capabilities.length}` : capabilities.length} effective
          </span>
        </div>
        {capabilities.length > 0 && (
          <input
            type="search"
            className="input input-sm w-full mb-3"
            placeholder="Filter capabilities…"
            aria-label="Filter capabilities"
            value={capQuery}
            onChange={e => setCapQuery(e.target.value)}
          />
        )}
        {capabilities.length === 0 ? (
          <p className="opacity-40 text-sm">No capabilities apply to this device.</p>
        ) : shownCapabilities.length === 0 ? (
          <p className="opacity-40 text-sm">No capabilities match “{capQuery.trim()}”.</p>
        ) : (
          <ul className="space-y-2">
            {shownCapabilities.map(c => (
              <li key={c.capability_id} className="flex flex-col sm:flex-row items-start gap-1 sm:gap-3">
                <span className="badge badge-ghost badge-sm font-mono mt-0.5">{c.name}</span>
                <div className="min-w-0 break-words">
                  <div className="text-sm font-medium">{c.label}</div>
                  {c.description && <div className="text-xs opacity-60">{c.description}</div>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Constraints (capabilities removed from this device) */}
      <div className="rounded-box border border-base-300 p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold">Constraints</h2>
          <div className="flex items-center gap-2">
            <span className="text-xs opacity-50">{constraints.length}</span>
            <button
              className="btn btn-xs btn-outline"
              disabled={capabilities.length === 0}
              onClick={() => { setShowAddForm(v => !v); setNewCapId(''); setNewReason('') }}
            >
              {showAddForm ? 'Cancel' : '+ Add'}
            </button>
          </div>
        </div>

        {showAddForm && (
          <div className="flex flex-col sm:flex-row gap-2 mb-4 p-3 bg-base-200 rounded-lg">
            <CapabilityCombobox
              options={capabilities}
              value={newCapId}
              onChange={setNewCapId}
              onPicked={() => reasonRef.current?.focus()}
              autoFocus
            />
            <input
              ref={reasonRef}
              className="input input-sm flex-1"
              placeholder="Reason (optional)"
              aria-label="Reason"
              value={newReason}
              onChange={e => setNewReason(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addConstraint()}
            />
            <button
              className="btn btn-sm btn-error"
              disabled={!newCapId || saving}
              aria-busy={saving}
              onClick={addConstraint}
            >
              {saving ? <Spinner size="xs" /> : 'Add'}
            </button>
          </div>
        )}

        {constraints.length === 0 ? (
          <p className="opacity-40 text-sm">No constraints — nothing removed from this device.</p>
        ) : (
          <ul className="space-y-2">
            {constraints.map(con => {
              const cap = capById.get(con.capability_id)
              return (
                <li key={con.capability_id} className="flex items-start gap-3">
                  <span className="badge badge-error badge-sm font-mono mt-0.5">
                    {cap ? cap.name : `#${con.capability_id}`}
                  </span>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{cap ? cap.label : `Capability #${con.capability_id}`}</div>
                    <div className="text-xs opacity-60">{con.reason ?? 'no reason given'}</div>
                  </div>
                  <button
                    className="btn btn-xs btn-ghost opacity-40 hover:opacity-100 hover:btn-error"
                    disabled={removingConstraint === con.capability_id}
                    aria-busy={removingConstraint === con.capability_id}
                    onClick={() => removeConstraint(con.capability_id)}
                    title="Remove constraint"
                    aria-label="Remove constraint"
                  >
                    {removingConstraint === con.capability_id ? <Spinner size="xs" /> : '✕'}
                  </button>
                </li>
              )
            })}
          </ul>
        )}
        <p className="text-xs opacity-40 mt-3">
          Constrained capabilities are excluded from the effective list above.
        </p>
      </div>

      {/* Cloud blob */}
      <div className="rounded-box border border-base-300 p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold">Cloud blob</h2>
          <span className="text-xs opacity-50">
            {device.cloud_synced_at
              ? `synced ${formatInstant(device.cloud_synced_at)}`
              : 'never synced'}
          </span>
        </div>
        {device.cloud_blob == null ? (
          <p className="opacity-40 text-sm">No blob yet — sync runs every 5 minutes, or use Sync now.</p>
        ) : (
          <pre className="bg-base-200 rounded-lg p-4 text-xs overflow-x-auto max-h-96 overflow-y-auto">
            {JSON.stringify(device.cloud_blob, null, 2)}
          </pre>
        )}
      </div>
    </div>
  )
}
